import type {Aggregation, AggregationResult} from "./Aggregation.js";
import type {Filter} from "./Filter.js";
import type {ListMetadata, ListQuery, SuggestionFilterJson} from "./RestClient.js";

/** Free-text search over the given paths, ranked by how closely each row matches. */
export interface Search {
    readonly text: string;
    readonly fields: readonly string[];
    readonly orderBy?: readonly string[];
}

export interface Query {
    readonly filter?: Filter;
    readonly orderBy?: string;
    readonly descending?: boolean;
    readonly maxRows?: number;
    readonly firstRow?: number;
    readonly appId?: number;
    readonly search?: Search;
    readonly aggregations?: readonly Aggregation[];
    /** Sub-path under the model path, for endpoints returning the same model, such as `public`. */
    readonly subPath?: string;
}

export interface QueryResult<E> {
    readonly entities: readonly E[];
    readonly totalCount: number | undefined;
    readonly aggregations: readonly AggregationResult[];
}

export const toListQuery = (query: Query): ListQuery => {
    const suggestionFilter = toSuggestionFilter(query.search);
    return {
        ...(query.filter === undefined ? {} : {where: query.filter.json}),
        ...(query.orderBy === undefined ? {} : {orderBy: orderByHeader(query)}),
        ...(query.maxRows === undefined ? {} : {maxRows: query.maxRows}),
        ...(query.firstRow === undefined ? {} : {firstRow: query.firstRow}),
        ...(query.appId === undefined ? {} : {appId: query.appId}),
        ...(suggestionFilter === undefined ? {} : {suggestionFilter}),
        ...(query.aggregations === undefined ? {} : {aggregations: query.aggregations.map(item => item.json)})
    };
};

export const totalCount = (metadata: ListMetadata): number | undefined =>
    typeof metadata.totalCount === "number" ? metadata.totalCount : undefined;

const orderByHeader = (query: Query): string =>
    query.descending === true ? `${query.orderBy} desc` : String(query.orderBy);

const toSuggestionFilter = (search: Search | undefined): SuggestionFilterJson | undefined => {
    if (search === undefined || search.text === "" || search.fields.length === 0) {
        return undefined;
    }
    return {searchString: search.text, fields: search.fields, orderBy: search.orderBy ?? []};
};
